import { ADMIN_NAV_ITEMS } from '../../../constants/adminNav.js';

/**
 * Resolve admin breadcrumb entries.
 * Each entry has a stable unique `id` (React key identity), distinct from `path` (navigation destination).
 */
export function resolveAdminCrumbs(pathname, t) {
  const crumbs = [{ id: 'dashboard', path: '/admin', label: t('admin.nav.dashboard') }];
  if (pathname === '/admin/prospection') {
    const prospection = ADMIN_NAV_ITEMS.find((item) => item.id === 'prospection' && !item.reserved);
    if (prospection) {
      crumbs.push({ id: 'prospection', path: prospection.path, label: t(prospection.labelKey) });
      crumbs.push({
        id: 'prospection-prospects',
        path: pathname,
        label: t('admin.nav.prospection.prospects'),
      });
    }
    return crumbs;
  }
  const exact = ADMIN_NAV_ITEMS.find((item) => item.path === pathname && !item.reserved);
  if (exact && exact.path !== '/admin') {
    crumbs.push({ id: exact.id, path: exact.path, label: t(exact.labelKey) });
    return crumbs;
  }
  const userMatch = pathname.match(/^\/admin\/users\/([^/]+)$/);
  if (userMatch) {
    const users = ADMIN_NAV_ITEMS.find((item) => item.id === 'users' && !item.reserved);
    if (users) {
      crumbs.push({ id: 'users', path: users.path, label: t(users.labelKey) });
      crumbs.push({ id: 'users-detail', path: pathname, label: t('admin.users.detail.crumb') });
    }
    return crumbs;
  }
  const propertyMatch = pathname.match(/^\/admin\/properties\/([^/]+)$/);
  if (propertyMatch) {
    const properties = ADMIN_NAV_ITEMS.find((item) => item.id === 'properties' && !item.reserved);
    if (properties) {
      crumbs.push({ id: 'properties', path: properties.path, label: t(properties.labelKey) });
      let propertyLabel = propertyMatch[1];
      try {
        propertyLabel = decodeURIComponent(propertyMatch[1]);
      } catch {
        /* keep raw */
      }
      crumbs.push({ id: 'properties-detail', path: pathname, label: propertyLabel });
    }
    return crumbs;
  }
  const importMatch = pathname === '/admin/prospection/import';
  if (importMatch) {
    const prospection = ADMIN_NAV_ITEMS.find((item) => item.id === 'prospection' && !item.reserved);
    if (prospection) {
      crumbs.push({ id: 'prospection', path: prospection.path, label: t(prospection.labelKey) });
      crumbs.push({
        id: 'prospection-import',
        path: pathname,
        label: t('admin.prospection.import.title'),
      });
    }
    return crumbs;
  }
  const followUpMatch = pathname === '/admin/prospection/follow-up';
  if (followUpMatch) {
    const prospection = ADMIN_NAV_ITEMS.find((item) => item.id === 'prospection' && !item.reserved);
    if (prospection) {
      crumbs.push({ id: 'prospection', path: prospection.path, label: t(prospection.labelKey) });
      crumbs.push({
        id: 'prospection-follow-up',
        path: pathname,
        label: t('admin.nav.prospection.followUp'),
      });
    }
    return crumbs;
  }
  const campaignDetailMatch = pathname.match(/^\/admin\/prospection\/campaigns\/([^/]+)$/);
  if (campaignDetailMatch) {
    const prospection = ADMIN_NAV_ITEMS.find((item) => item.id === 'prospection' && !item.reserved);
    if (prospection) {
      crumbs.push({ id: 'prospection', path: prospection.path, label: t(prospection.labelKey) });
      crumbs.push({
        id: 'prospection-campaigns',
        path: '/admin/prospection/campaigns',
        label: t('admin.nav.prospection.campaigns'),
      });
      crumbs.push({
        id: 'prospection-campaign-detail',
        path: pathname,
        label: t('admin.prospection.campaign.detail.crumb'),
      });
    }
    return crumbs;
  }
  const letterTemplatesMatch = pathname === '/admin/prospection/letter-templates';
  if (letterTemplatesMatch) {
    const prospection = ADMIN_NAV_ITEMS.find((item) => item.id === 'prospection' && !item.reserved);
    if (prospection) {
      crumbs.push({ id: 'prospection', path: prospection.path, label: t(prospection.labelKey) });
      crumbs.push({
        id: 'prospection-letter-templates',
        path: pathname,
        label: t('admin.nav.prospection.letterTemplates'),
      });
    }
    return crumbs;
  }
  const letterTemplateNewMatch = pathname === '/admin/prospection/letter-templates/new';
  if (letterTemplateNewMatch) {
    const prospection = ADMIN_NAV_ITEMS.find((item) => item.id === 'prospection' && !item.reserved);
    if (prospection) {
      crumbs.push({ id: 'prospection', path: prospection.path, label: t(prospection.labelKey) });
      crumbs.push({
        id: 'prospection-letter-templates',
        path: '/admin/prospection/letter-templates',
        label: t('admin.nav.prospection.letterTemplates'),
      });
      crumbs.push({
        id: 'prospection-letter-template-new',
        path: pathname,
        label: t('admin.prospection.letterTemplate.new.crumb'),
      });
    }
    return crumbs;
  }
  const letterTemplateDetailMatch = pathname.match(/^\/admin\/prospection\/letter-templates\/([^/]+)$/);
  if (letterTemplateDetailMatch && letterTemplateDetailMatch[1] !== 'new') {
    const prospection = ADMIN_NAV_ITEMS.find((item) => item.id === 'prospection' && !item.reserved);
    if (prospection) {
      crumbs.push({ id: 'prospection', path: prospection.path, label: t(prospection.labelKey) });
      crumbs.push({
        id: 'prospection-letter-templates',
        path: '/admin/prospection/letter-templates',
        label: t('admin.nav.prospection.letterTemplates'),
      });
      crumbs.push({
        id: 'prospection-letter-template-detail',
        path: pathname,
        label: t('admin.prospection.letterTemplate.editor.crumb'),
      });
    }
    return crumbs;
  }
  const campaignsMatch = pathname === '/admin/prospection/campaigns';
  if (campaignsMatch) {
    const prospection = ADMIN_NAV_ITEMS.find((item) => item.id === 'prospection' && !item.reserved);
    if (prospection) {
      crumbs.push({ id: 'prospection', path: prospection.path, label: t(prospection.labelKey) });
      crumbs.push({
        id: 'prospection-campaigns',
        path: pathname,
        label: t('admin.nav.prospection.campaigns'),
      });
    }
    return crumbs;
  }
  const prospectMatch = pathname.match(/^\/admin\/prospection\/([^/]+)$/);
  if (
    prospectMatch &&
    prospectMatch[1] !== 'import' &&
    prospectMatch[1] !== 'follow-up' &&
    prospectMatch[1] !== 'campaigns' &&
    prospectMatch[1] !== 'letter-templates'
  ) {
    const prospection = ADMIN_NAV_ITEMS.find((item) => item.id === 'prospection' && !item.reserved);
    if (prospection) {
      crumbs.push({ id: 'prospection', path: prospection.path, label: t(prospection.labelKey) });
      crumbs.push({
        id: 'prospection-prospects',
        path: prospection.path,
        label: t('admin.nav.prospection.prospects'),
      });
      crumbs.push({
        id: 'prospection-detail',
        path: pathname,
        label: t('admin.prospection.detail.crumb'),
      });
    }
  }
  return crumbs;
}
